/**
 * Un item de la toolbox posé dans un éditeur fabric — au clic ou au drop. La logique
 * partagée entre les DEUX éditeurs (images : fabric_controller.ts, vidéo :
 * composition_editor/fabric_controller.js) : ce que devient chaque sorte d'item (texte,
 * forme, image, image à fournir), et le binding des médias dynamiques.
 *
 * Un média se pose sur une CIBLE (lib toolbox_placement) : le canvas en connaît deux, le
 * remplacement d'un calque et l'insertion ; les autres (fond de page, séquences) sont celles
 * de l'éditeur, qu'il prend en hook (placeElsewhere), avec son rituel d'après-mutation :
 * export des layers côté images, overlays côté vidéo.
 */
import { rulesOf, type DropHandler, type FabricEditor } from "@frabr/fabric-editor";
import type { FabricObject } from "fabric";
import type { FabricControls } from "./fabric_controls";
import { applyDynamicBinding, type DynamicMedia } from "./fabric_bindings";
import { mediumMeta } from "./composition_document";
import { toDragPayload, type ToolboxItemData } from "./toolbox_drop_zone";
import type { Placement } from "./toolbox_placement";
import { shapeCatalogsSettled } from "./shape_catalogs";

export interface ToolboxHost {
  editor: FabricEditor;
  controls: FabricControls;
  dropHandler: DropHandler;
  /** Les défauts de CET éditeur pour les items qui n'en portent pas (texte, couleur). */
  defaults?: { text?: string; fill?: string };
  /** Un calque vient d'être AJOUTÉ (vidéo : il reçoit son overlay) — à la place de
   *  onChanged, qui tient lieu d'ajout quand l'éditeur ne le fournit pas. */
  onAdded?(obj: FabricObject): void;
  /** Le document a changé (images : export des layers ; vidéo : resync des overlays). */
  onChanged(): void;
  /** Un média de la médiathèque arrive sur le canvas : son url et l'id de son CM. */
  onMedia?(url: string, id: string): void;
  /** La page affichée — une insertion visant une autre page n'a plus de sens. */
  currentPage?(): string | null;
  /** Les cibles hors canvas de l'éditeur (fond de page, séquences) : vrai si posé. */
  placeElsewhere?(item: Item, placement: Placement): boolean | Promise<boolean>;
}

type Item = ToolboxItemData & { dynamic?: DynamicMedia };

const acceptsImage = (obj: unknown): obj is FabricObject =>
  Boolean(obj) && rulesOf(obj as FabricObject).options.includes("image");

/**
 * La cible d'un média sur le canvas : le calque sélectionné s'il prend une image, sinon un
 * calque de plus — sur la page `page` (éditeur images), au visuel sans page (vidéo).
 */
export function canvasPlacement(editor: FabricEditor, page: string | null = null): Placement {
  const selected = editor.selection.current;
  const layer = selected?.get("layerId");
  if (acceptsImage(selected) && layer) return { action: "replace", layer };

  return { action: "insert", page };
}

/** Une vidéo ne se pose jamais sur le canvas (la timeline, côté vidéo). */
const isVideo = (item: Item) => item.type === "media" && item.media_type === "video";

function noteMedia(host: ToolboxHost, item: Item): void {
  if (item.type === "media" && item.url && item.id) host.onMedia?.(item.url, String(item.id));
}

/** Le drag d'un item commence : le DropHandler arme sa preview et ses capacités. */
export function armToolboxDrag(host: ToolboxHost, item: Item): void {
  if (!item?.type || isVideo(item)) return;

  noteMedia(host, item);
  const payload = toDragPayload(item, host.defaults);
  if (payload) host.dropHandler.prepareDrag(payload);
}

/**
 * Un clic sur un item : il est posé. Un média va à sa cible — celle qu'il porte (figée au
 * dépôt d'un upload), sinon celle du canvas à l'instant du clic.
 */
export async function placeToolboxItem(host: ToolboxHost, item: Item, placement?: Placement | null): Promise<void> {
  if (!item?.type) return;
  if (item.type === "media") {
    await placeMedia(host, item, placement === undefined ? canvasPlacement(host.editor) : placement);
    return;
  }

  const payload = toDragPayload(item, host.defaults);
  // Une forme cliquée avant l'arrivée de son catalogue serait un rectangle
  if (payload?.kind === "shape") await shapeCatalogsSettled();
  switch (payload?.kind) {
    case "text":
      return added(host, host.controls.addText(payload.opts));
    case "shape":
      return added(host, host.controls.addShape({ ...payload.opts, shapeType: payload.shapeType }));
    case "userSlot":
      return added(host, host.editor.layers.addUserSlot(payload.opts));
  }
}

/**
 * Un média posé sur une cible : vrai s'il y est arrivé. Une cible disparue (calque supprimé,
 * page quittée) ne se rabat sur rien. Une vidéo ne se pose jamais sur le canvas.
 */
export async function placeMedia(host: ToolboxHost, item: Item, placement: Placement | null): Promise<boolean> {
  if (!placement || !item?.url) return false;

  if (placement.action !== "replace" && placement.action !== "insert") {
    return (await host.placeElsewhere?.(item, placement)) ?? false;
  }
  if (isVideo(item)) return false;

  const cm = { url: item.url, id: item.id };
  if (placement.action === "replace") {
    const target = host.editor.layers.findById(placement.layer);
    if (!acceptsImage(target)) return false;

    noteMedia(host, item);
    await host.controls.replaceImage(target, cm);
    applyDynamicBinding(host.editor, target, item.dynamic);
    host.onChanged();
    return true;
  }

  if (placement.page && host.currentPage && host.currentPage() !== placement.page) return false;

  noteMedia(host, item);
  added(host, await host.controls.addImage(cm), item.dynamic);
  return true;
}

/**
 * Un item lâché sur le canvas. Le DropHandler a déjà prévenu l'éditeur (onSuccess) ; reste
 * le binding d'un média dynamique, et l'enregistrement d'un calque ajouté.
 */
export async function dropToolboxItem(host: ToolboxHost, e: DragEvent, item: Item): Promise<void> {
  const { dropHandler } = host;
  // Une forme lâchée avant l'arrivée de son catalogue serait un rectangle
  if (item?.type === "shape") await shapeCatalogsSettled();
  let result;
  if (dropHandler.isExternalDrag) {
    result = await dropHandler.completeDrag(e);
  } else if (item?.url && !isVideo(item)) {
    // Le dragstart a été manqué : la carte n'a pas été armée, on lâche l'image telle quelle
    noteMedia(host, item);
    result = await dropHandler.dropImage(item.url, e, { imageMeta: mediumMeta(item) });
  }
  if (!result) return;

  const bound = applyDynamicBinding(host.editor, result.object, item.dynamic);
  if (result.kind === "add" && host.onAdded) host.onAdded(result.object);
  else if (bound) host.onChanged();
}

/** Le rituel d'un ajout : binding éventuel AVANT l'enregistrement (l'overlay le sérialise).
 *  `object` : les calques de la lib portent sa propre copie des types fabric. */
function added(host: ToolboxHost, obj: object | null | undefined, dynamic?: DynamicMedia): void {
  if (!obj) return;

  applyDynamicBinding(host.editor, obj as FabricObject, dynamic);
  if (host.onAdded) host.onAdded(obj as FabricObject);
  else host.onChanged();
}
